import { createEffect, onCleanup, onMount, reactive } from 'cdui-js';
import { changeFile, lastProcess, nextProcess, pState, jumpVisit } from '../../store';
import {
  composeSignatureOnAttachment,
  filingRecordCompose,
  signFieldFileAcquire,
  verifyOpenProfileFingerprint,
} from '../../../../../../api/open-account';
import { uploadFile } from '../../../../../../api/common';
import { showToast, showToastError } from '../../../../components/Toast';
import { showModal } from '../../../../components/Modal';
import i18n from '../../../../../../i18n';
import { replaceTemplate } from '@china-difi/lib/template';
import { isH5User } from '../../../../../../stores/user';
import { blockBack } from '../../../../../../lib/browser';
import { loadSzcaCertCache, restoreKeyPairToPState } from '../risk/szca-face';
import { MAINLAND_RISK_SZCA_STEP } from '../risk/openSzcaAuthViaApp';
import { fillSignatureBoard, paintSignatureHint } from '../../../paintSignatureHint';

/** canvas dataURL → PNG File；强制 image/png，避免 iOS FormData 丢 MIME */
const dataURLtoFile = (dataUrl: string, filename: string): File => {
  const data = dataUrl.split(',')[1] || '';
  const binary = atob(data);
  const u8 = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) u8[i] = binary.charCodeAt(i);
  const blob = new Blob([u8], { type: 'image/png' });
  const name = filename.endsWith('.png') ? filename : `${filename}.png`;
  return new File([blob], name, { type: 'image/png' });
};

const finalSignatureBase64 = (canvas: HTMLCanvasElement) => {
  const OUT_W = 400;
  const OUT_H = 100;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  // Step1: 扫描像素找签名边界，去掉空白区域（整张画布里签名只占一小部分）
  const w = canvas.width;
  const h = canvas.height;
  const px = canvas.getContext('2d')!.getImageData(0, 0, w, h).data;
  let x0 = w,
    y0 = h,
    x1 = 0,
    y1 = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (px[i] < 245 || px[i + 1] < 245 || px[i + 2] < 245) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x0 > x1 || y0 > y1) return '';

  // Step2: 加内边距
  const pad = Math.round(12 * dpr);
  x0 = Math.max(0, x0 - pad);
  y0 = Math.max(0, y0 - pad);
  x1 = Math.min(w - 1, x1 + pad);
  y1 = Math.min(h - 1, y1 + pad);
  const srcW = x1 - x0 + 1; // 裁剪区物理宽（签名横向延伸）
  const srcH = y1 - y0 + 1; // 裁剪区物理高（签名纵向延伸）

  // Step3: 旋转后 srcW→输出高，srcH→输出宽，等比缩放适配 OUT_W×OUT_H
  const fitScale = Math.min(OUT_W / (srcH / dpr), OUT_H / (srcW / dpr));
  const fitW = Math.round((srcH / dpr) * fitScale);
  const fitH = Math.round((srcW / dpr) * fitScale);
  const fitX = Math.round((OUT_W - fitW) / 2);
  const fitY = Math.round((OUT_H - fitH) / 2);

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = OUT_W;
  tempCanvas.height = OUT_H;
  const ctx = tempCanvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, OUT_W, OUT_H);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // 逆时针旋转 90°，一次 drawImage 完成裁剪 + 旋转 + 缩放
  ctx.save();
  ctx.translate(fitX, fitY + fitH);
  ctx.rotate(-Math.PI / 2);
  ctx.drawImage(canvas, x0, y0, srcW, srcH, 0, 0, fitH, fitW);
  ctx.restore();

  return tempCanvas.toDataURL('image/png', 0.9);
};

/** 签名区可用尺寸：相对 #h5-shell / 父容器，避免 PC 壳下 100vw/100vh 把按钮裁掉 */
const SIGNATURE_HEADER_OFFSET = 104;
const SIGNATURE_MOBILE_HEADER = 54;
/** 横屏上下留白（竖屏看是左右）；画板原先贴边 */
const SIGNATURE_EDGE_INSET = 12;

const getSignatureFallbackSize = () => {
  const shell = typeof document !== 'undefined' ? document.getElementById('h5-shell') : null;
  if (shell?.classList.contains('h5-shell--pc')) {
    return {
      width: shell.clientWidth || 430,
      height: Math.max(0, shell.clientHeight - SIGNATURE_MOBILE_HEADER),
    };
  }
  return {
    width: typeof window !== 'undefined' ? window.innerWidth : 390,
    height: typeof window !== 'undefined' ? Math.max(0, window.innerHeight - SIGNATURE_HEADER_OFFSET) : 500,
  };
};

export const Signature = () => {
  const state = reactive({
    base64: '',
    loading: false,
  });
  const layout = reactive(getSignatureFallbackSize());

  const goBackToRiskDisclosure = () => {
    // 仅清内存；localStorage 保留至 finishOpenAccount（CA 过期靠指纹校验强制重申）
    pState.certSn = '';
    pState.certDn = '';
    if (pState.editRefusedMode) {
      jumpVisit(pState.current, { editRefusedPhase: 'risk' });
      return;
    }
    // 保留签名页在访问栈中，返回可原路回来（勿 setRecords 清成 0..n）
    jumpVisit(MAINLAND_RISK_SZCA_STEP);
  };

  const showCaExpiredModal = () => {
    showModal({
      disableClickMask: true,
      title: i18n.OpenProgress.Risk.Szca.ToastCaExpired,
      content: () => '',
      confirmText: i18n.OpenProgress.Common.Confirm,
      onConfirm: () => {
        goBackToRiskDisclosure();
      },
    });
  };

  const handleNext = async () => {
    if (state.loading) return;
    isDrawing = false;
    state.loading = true;
    try {
      const base64 = generateSignature();
      if (!base64) {
        showToast(i18n.OpenProgress.Transfer.Signature.ToastCompleteFirst);
        return;
      }
      state.base64 = base64;

      const base64Data = base64.split('base64,')[1];
      const isMainland = pState.uploadLicForm.idCardType === 'MAINLAND_CHINA';

      // 大陆：先校验 CA 是否过期，再用本地 certDn/certSn 做签章合成
      if (isMainland) {
        const fp = await verifyOpenProfileFingerprint();
        if (!fp?.valid) {
          showCaExpiredModal();
          return;
        }

        const cached = loadSzcaCertCache(pState.id);
        const certDn = cached?.certDn || pState.certDn;
        const certSn = cached?.certSn || pState.certSn;
        if (!certDn || !certSn) {
          showToast(i18n.OpenProgress.Risk.Szca.ToastCertCacheMissing);
          return;
        }

        pState.certDn = certDn;
        pState.certSn = certSn;
        if (!pState.keyPair && cached?.privateKeyPem && cached?.publicKeyPem) {
          await restoreKeyPairToPState(pState, {
            certDn,
            privateKeyPem: cached.privateKeyPem,
            publicKeyPem: cached.publicKeyPem,
          });
        }

        const data = await signFieldFileAcquire(certDn, base64Data, certSn, pState.id);
        const { createPKCS1 } = await import('../../phases/open-cert');
        const p1SignData = createPKCS1(pState.keyPair, data.fileHash);
        await filingRecordCompose(certDn, certSn, data.fileID, p1SignData, pState.id);
      }

      const signFile = dataURLtoFile(base64, 'sign.png');
      // 大陆始终立刻贴图。非大陆编辑重签（editRefused）签完即结束、不再进绑卡，也立刻贴图。
      // 首次开户只上传贴纸，绑卡成功后再 compose。
      if (isMainland || pState.editRefusedMode) {
        await composeSignatureOnAttachment(pState.id, signFile);
      } else {
        const uploaded = await uploadFile({
          useFor: 'signature_sticker',
          file: signFile,
          busId: pState.id,
          isOCR: false,
        });
        const filePath = uploaded?.filePath;
        if (!filePath) {
          showToast(i18n.OpenProgress.Transfer.Signature.ToastCompleteFirst);
          return;
        }
        changeFile('signature_sticker', filePath);
      }

      nextProcess();
    } catch (e) {
      showToastError(e);
    } finally {
      state.loading = false;
    }
  };
  // 1. 创建 Canvas 引用，用于操作画板
  let canvasRef = null;
  let rootRef: HTMLDivElement | null = null;
  // 签名状态：是否正在绘制、画笔上下文、起始坐标
  let isDrawing = false;
  let ctx = null;
  let lastX = 0;
  let lastY = 0;
  let hasStroke = false;

  const paintEmptyBoard = (cssWidth: number, cssHeight: number) => {
    if (!ctx) return;
    fillSignatureBoard(ctx, cssWidth, cssHeight);
    paintSignatureHint(ctx, cssWidth, cssHeight, i18n.OpenProgress.Transfer.Signature.SignHintPrefix);
  };

  const syncSignatureLayout = () => {
    const el = rootRef;
    const shell = document.getElementById('h5-shell');
    const width = el?.clientWidth || shell?.clientWidth || window.innerWidth;
    let height: number;
    if (el && shell) {
      height = Math.max(0, Math.round(shell.getBoundingClientRect().bottom - el.getBoundingClientRect().top));
    } else if (shell?.classList.contains('h5-shell--pc')) {
      height = Math.max(0, shell.clientHeight - SIGNATURE_MOBILE_HEADER);
    } else {
      height = Math.max(0, window.innerHeight - SIGNATURE_HEADER_OFFSET);
    }
    if (width > 0) layout.width = width;
    if (height > 0) layout.height = height;
  };

  // 2. 初始化画板（组件挂载后执行；布局变化时重建）
  createEffect(() => {
    const canvas = canvasRef;
    if (!canvas) return;
    // 追踪壳宽高，PC 壳 / resize 后重算 canvas 物理像素
    void layout.width;
    void layout.height;

    // 修复 Canvas 高清模糊问题；DPR 最多取 2，超高清屏没必要，只会增大体积
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    // 实际宽高 = 视觉宽高 * 设备像素比
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    // 缩放上下文，适配高清屏
    ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);

    // 初始化画笔样式
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    hasStroke = false;
    paintEmptyBoard(rect.width, rect.height);
  });

  // 签名页锁滚：H5 Safari / iOS App WebView 均禁止跟手滚动与橡皮筋
  onMount(() => {
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      bodyTouchAction: body.style.touchAction,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';
    // iOS：fixed 住 body，避免视觉视口跟手弹跳
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    const preventScroll = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const touchOpts: AddEventListenerOptions = { passive: false, capture: true };
    document.addEventListener('touchmove', preventScroll, touchOpts);

    let removeCanvasStyle: (() => void) | undefined;
    const bindCanvas = () => {
      const el = canvasRef as HTMLCanvasElement | null;
      if (!el || removeCanvasStyle) return;
      el.style.touchAction = 'none';
      removeCanvasStyle = () => {
        el.style.touchAction = '';
      };
    };
    bindCanvas();
    const timer = window.setTimeout(bindCanvas, 0);

    syncSignatureLayout();
    const shell = document.getElementById('h5-shell');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncSignatureLayout()) : null;
    if (ro) {
      if (rootRef) ro.observe(rootRef);
      if (shell) ro.observe(shell);
    }
    window.addEventListener('resize', syncSignatureLayout);
    // ref 可能晚于 onMount，下一帧再量一次并挂观察
    const layoutTimer = window.setTimeout(() => {
      syncSignatureLayout();
      if (ro && rootRef) ro.observe(rootRef);
    }, 0);

    // H5：拦截 iOS Safari 左缘侧滑返回（App 不处理）
    const unlockBack = isH5User() ? blockBack() : undefined;

    onCleanup(() => {
      window.clearTimeout(timer);
      window.clearTimeout(layoutTimer);
      removeCanvasStyle?.();
      ro?.disconnect();
      window.removeEventListener('resize', syncSignatureLayout);
      document.removeEventListener('touchmove', preventScroll, touchOpts);
      unlockBack?.();
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      body.style.touchAction = prev.bodyTouchAction;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    });
  });

  // 3. 开始绘制（触摸/鼠标按下）
  const startDraw = (e) => {
    if (state.loading) return;
    if (e.cancelable) e.preventDefault();
    isDrawing = true;
    // 获取相对于 Canvas 的坐标（兼容触摸/鼠标事件）
    const { clientX, clientY } = e.touches ? e.touches[0] : e;
    const rect = canvasRef.getBoundingClientRect();
    lastX = clientX - rect.left;
    lastY = clientY - rect.top;
    if (!hasStroke && ctx) {
      fillSignatureBoard(ctx, rect.width, rect.height);
      hasStroke = true;
    }
  };

  // 4. 绘制中（触摸/鼠标移动）
  const drawing = (e) => {
    if (e.cancelable) e.preventDefault();
    if (state.loading || !isDrawing || !ctx) return;
    const { clientX, clientY } = e.touches ? e.touches[0] : e;
    const rect = canvasRef.getBoundingClientRect();
    const currentX = clientX - rect.left;
    const currentY = clientY - rect.top;

    // 绘制线条（从上次坐标到当前坐标）
    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(currentX, currentY);
    ctx.stroke();

    // 更新上次坐标
    lastX = currentX;
    lastY = currentY;
  };

  // 5. 结束绘制（触摸/鼠标松开）
  const endDraw = () => {
    isDrawing = false;
  };

  // 6. 清空签名画板
  const clearCanvas = () => {
    if (state.loading) return;
    const canvas = canvasRef;
    if (!ctx || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    hasStroke = false;
    paintEmptyBoard(rect.width, rect.height);
  };

  // 7. 生成签名图片
  const generateSignature = (): string => {
    const canvas = canvasRef;
    if (!canvas || !hasStroke) return '';

    return finalSignatureBase64(canvas);

    // return outCanvas.toDataURL('image/jpeg', 0.92);
  };

  return (
    <div
      class="open-progress-signature"
      ref={(el) => {
        rootRef = el;
      }}
      style={{
        position: 'relative',
        width: '100%',
        height: `${layout.height}px`,
        overflow: 'hidden',
        'touch-action': 'none',
        'overscroll-behavior': 'none',
        '-webkit-user-select': 'none',
        'user-select': 'none',
      }}
    >
      {/* 横屏操作区：相对壳宽高旋转，勿用 100vw（PC 壳远小于视口） */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: `${layout.height}px`,
          height: `${Math.max(0, layout.width - SIGNATURE_EDGE_INSET * 2)}px`,
          overflow: 'hidden',
          transform: 'translate(-50%, -50%) rotate(90deg)',
        }}
      >
        <div class="flex flex-column height-100">
          <div class="flex justify-center align-center gap-12px" style={{ height: '40px' }}>
            {replaceTemplate(i18n.OpenProgress.Transfer.Signature.Consent, { company: i18n.OpenProgress.Common.DefaultCompanyName })}
            <button
              class="default-btn padding-x"
              style={{ height: '32px' }}
              onclick={() => clearCanvas()}
              disabled={state.loading}
            >
              {i18n.OpenProgress.Common.Rewrite}
            </button>
          </div>
          <div class="flex-auto"></div>
          <div class="flex justify-center">
            <div
              class="flex padding-x-L align-center"
              style={{ gap: '10px', height: '60px', width: `${Math.max(0, layout.width - SIGNATURE_EDGE_INSET * 2)}px` }}
            >
              <button
                class="default-btn btn-rounded"
                style={{ height: '48px', flex: 1 }}
                onclick={() => lastProcess()}
                disabled={state.loading}
              >
                {i18n.OpenProgress.Common.Prev}
              </button>

              <button class="primary-btn btn-rounded" style={{ height: '48px', flex: 2 }} onclick={handleNext} disabled={state.loading}>
                {state.loading ? (
                  <svg
                    style={{ animation: 'spin 1s linear infinite' }}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.886 2.06125C11.1496 2.03207 11.414 2.10882 11.6211 2.2746C11.8281 2.44038 11.9608 2.68162 11.99 2.94525C12.0192 3.20887 11.9424 3.47329 11.7766 3.68033C11.6109 3.88737 11.3696 4.02007 11.106 4.04925C9.07764 4.2816 7.21483 5.28056 5.899 6.84158C4.58316 8.40261 3.91385 10.4076 4.0281 12.446C4.14236 14.4844 5.03153 16.4021 6.51357 17.8063C7.99561 19.2105 9.95838 19.995 12 19.9992V21.9992C6.477 21.9992 2 17.5232 2 11.9992C2 6.89325 5.848 2.62125 10.886 2.06225V2.06125Z"
                      fill="#fff"
                      fill-opacity="0.5"
                    />
                    <path
                      d="M16.322 4.04002C16.4737 3.82259 16.7056 3.67432 16.9667 3.62781C17.2277 3.58131 17.4965 3.64037 17.714 3.79202C19.0378 4.71269 20.119 5.94012 20.8654 7.36946C21.6118 8.79879 22.001 10.3876 22 12C22 17.523 17.523 22 12 22V20C13.7035 20.0003 15.3626 19.4567 16.7358 18.4485C18.1089 17.4403 19.1244 16.0201 19.6343 14.3947C20.1442 12.7693 20.122 11.0236 19.5708 9.41168C19.0197 7.79978 17.9684 6.40591 16.57 5.43302C16.4622 5.35788 16.3703 5.26223 16.2994 5.15154C16.2286 5.04085 16.1803 4.91729 16.1573 4.78792C16.1343 4.65855 16.137 4.52591 16.1652 4.39758C16.1935 4.26925 16.2468 4.14775 16.322 4.04002Z"
                      fill="#fff"
                    />
                  </svg>
                ) : (
                  i18n.OpenProgress.Common.Next
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      <canvas
        ref={(el) => (canvasRef = el)}
        style={{
          position: 'absolute',
          right: `${40 + SIGNATURE_EDGE_INSET}px`,
          top: 0,
          width: `calc(100% - 70px - 40px - ${SIGNATURE_EDGE_INSET * 2}px)`,
          height: `${layout.height}px`,
          'touch-action': 'none',
          'pointer-events': state.loading ? 'none' : 'auto',
        }}
        class="border"
        onmousedown={startDraw}
        onmousemove={drawing}
        onmouseup={endDraw}
        onmouseleave={endDraw}
        // 触摸事件（适配移动端）
        ontouchstart={startDraw}
        ontouchmove={drawing}
        ontouchend={endDraw}
      >
        {i18n.OpenProgress.Transfer.Signature.SignatureArea}
      </canvas>

      {/* <img style={{ position: 'absolute', right: 0, top: 0 }} src={''} /> */}
    </div>
  );
};
